import { CheckOutlined, CloseOutlined, ExceptionOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import { tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { App, Button, Flex, Modal, Popconfirm, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { filesize } from 'filesize';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { CodeEditor } from '../../../components/CodeEditor.tsx';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import { PdfPreviewModal } from '../../../components/PdfPreviewModal.tsx';
import { OrderFileType } from '../../../graphql/generated/graphql.ts';
import { useObjectUrlResource } from '../../../hooks/useObjectUrlResource.ts';
import { printErrorShown, usePrinter } from '../../../hooks/usePrinter.ts';
import { useTextResource } from '../../../hooks/useTextResource.ts';
import { PrintButton } from '../../../PrintButton.tsx';
import { tableActionCell } from '../../../styles/globalCss.ts';
import { SHORT_DATETIME_FORMAT } from '../../../utils/dateFormatUtils.ts';
import { translateOrderFileType } from '../../../utils/enumHelpers.ts';
import { fetchPrintable } from '../../../utils/print.ts';
import { hasSomeRole, Role } from '../../../utils/user.ts';
import { useCancelOrderInvoiceMutation } from './useCancelOrderInvoiceMutation.tsx';
import { useMarkOrderFilePrintedMutation } from './useMarkOrderFilePrintedMutation.tsx';
import type { Order, OrderFile } from './useRequestDetailsQuery.tsx';

export const FilesTab = ({ order, requestId, onRefetchRequested }: { order: Order; requestId: string; onRefetchRequested: () => void }) => {
  const { message } = App.useApp();
  const auth = useAuth();
  const { print } = usePrinter();
  const hasUserRole = hasSomeRole([Role.ROLE_LR_USER], auth.user);
  const [cancelOrderInvoiceMutation] = useCancelOrderInvoiceMutation();
  const [markOrderFilePrintedMutation] = useMarkOrderFilePrintedMutation();

  const printableOrderFiles = order.orderFiles.filter(it => it.printable);

  const [previewOrderFile, setPreviewOrderFile] = useState<OrderFile | null>(null);

  const closeOrderFile = () => {
    resetHl7Content();
    cleanupPdfPreviewUrl();
    setPreviewOrderFile(null);
  };

  const [hl7Content, fetchHl7Content, resetHl7Content, hl7ContentLoading] = useTextResource(() =>
    message.error('Es ist ein Fehler beim Laden des Dokuments aufgetreten')
  );
  const {
    objectUrl: pdfPreviewUrl,
    fetchAndSet: fetchPdfPreviewUrl,
    cleanup: cleanupPdfPreviewUrl,
    loading: pdfPreviewLoading,
  } = useObjectUrlResource(() => message.error('Es ist ein Fehler beim Laden des Dokuments aufgetreten'), 'application/pdf');

  const printOrderFiles = async (orderFiles: OrderFile[]) => {
    let error = false;

    for (const file of orderFiles) {
      try {
        const buffer = await fetchPrintable(`/rest/order-file/${file.id}`, auth.user?.access_token);
        await print(buffer, 'a4', { title: file.name, requestId, orderId: order.id });
      } catch (e) {
        // the AIS failing once fails the rest, and the printer has said so
        if (printErrorShown(e)) {
          return;
        }
        error = true;
      }
    }

    if (error) {
      message.error('Beim Drucken ist ein Fehler aufgetreten');
      return;
    }

    message.success('Druckauftrag erstellt');

    try {
      for (const file of orderFiles) {
        await markOrderFilePrintedMutation({
          variables: {
            orderFileId: file.id,
          },
        });
      }

      onRefetchRequested();
    } catch (_e) {
      message.error('Der Druckstatus kann nicht gesetzt werden');
    }
  };

  const fileColumns: ColumnsType<OrderFile> = [
    {
      title: 'Bezeichnung',
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: v => <Typography.Text style={{ fontWeight: 500 }}>{v}</Typography.Text>,
    },
    {
      title: 'Typ',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => a.type.localeCompare(b.type),
      render: value => translateOrderFileType(value),
    },
    {
      title: 'Dateigröße',
      dataIndex: 'size',
      key: 'size',
      sorter: (a, b) => a.size - b.size,
      render: (_, record) => <span className={tabularNums}>{filesize(record.size, { locale: 'de' }) as string}</span>,
    },
    {
      title: 'Letzter Druck am',
      dataIndex: 'printedAt',
      key: 'printedAt',
      sorter: (a, b) => (a.printedAt ?? '').localeCompare(b.printedAt ?? ''),
      render: value => (value ? <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span> : ''),
    },
    {
      title: 'Elekt. übertragen',
      dataIndex: 'pickup',
      key: 'pickup',
      sorter: (a, b) => Number(a.pickup) - Number(b.pickup),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      className: tableActionCell,
      render: (_, record) => (
        <Flex justify="end">
          <Button icon={<EyeOutlined />} type="link" onClick={() => showOrderFile(record)} />
          {record.printable && <PrintButton icon={<PrinterOutlined />} type="link" onClick={() => printOrderFiles([record])} />}
        </Flex>
      ),
    },
  ];

  const cancelOrderInvoice = async (orderId: string) => {
    try {
      await cancelOrderInvoiceMutation({
        variables: {
          orderId: orderId,
        },
      });

      onRefetchRequested();
      message.success('Die Rechnung wurde erfolgreich storniert');
    } catch (_e) {
      message.error('Die Rechnung konnte nicht storniert werden');
    }
  };

  const showOrderFile = (orderFile: OrderFile) => {
    const endpoint = `${window._env_.API_URL}/rest/order-file/${orderFile.id}`;
    if (orderFile.type === OrderFileType.HL7) {
      fetchHl7Content(endpoint);
    }
    if (
      orderFile.type === OrderFileType.TRANSFER_DOCUMENT ||
      orderFile.type === OrderFileType.FORM_DOCUMENT ||
      orderFile.type === OrderFileType.INVOICE
    ) {
      fetchPdfPreviewUrl(endpoint);
    }
    setPreviewOrderFile(orderFile);
  };

  const filteredOrderFiles = order.orderFiles
    // do not show etron files
    .filter(it => it.type !== OrderFileType.ETRON)
    // do not show HL7 files for standard users
    .filter(it => !(it.type === OrderFileType.HL7 && hasUserRole));

  return filteredOrderFiles.length ? (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Flex gap="small">
        <PrintButton icon={<PrinterOutlined />} disabled={!printableOrderFiles.length} onClick={() => printOrderFiles(printableOrderFiles)}>
          Alle Dokumente drucken
        </PrintButton>
        {hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_FRONT_DESK], auth.user) &&
          order.invoiceNumber &&
          !order.invoiceCancellationNumber && (
            <Popconfirm
              title="Rechnung stornieren?"
              onConfirm={() => cancelOrderInvoice(order.id)}
              okText="Ja, stornieren"
              okButtonProps={{ danger: true }}
              cancelText="Abbrechen"
              placement="bottom"
            >
              <Button icon={<ExceptionOutlined />} danger>
                Storno-Rechnung erzeugen
              </Button>
            </Popconfirm>
          )}
      </Flex>
      <Table<OrderFile>
        // columns as wide as their content, like the labels tab
        tableLayout="auto"
        scroll={{ y: '300px', x: 'max-content' }}
        rowKey={file => file.id}
        size="small"
        showSorterTooltip={false}
        dataSource={filteredOrderFiles}
        pagination={false}
        columns={fileColumns}
      />
      <Modal
        title={previewOrderFile?.fileName}
        open={hl7Content.length > 0 || hl7ContentLoading}
        width="80%"
        style={{ maxWidth: '1200px' }}
        cancelButtonProps={{ hidden: true }}
        okButtonProps={{ icon: <CloseOutlined /> }}
        okText="Schließen"
        onOk={closeOrderFile}
        onCancel={closeOrderFile}
        destroyOnHidden
      >
        {hl7ContentLoading ? (
          <LoadingIndicator />
        ) : (
          <CodeEditor
            mode="hl7"
            value={hl7Content}
            height="400px"
            editable={false}
            // wait for the modal transition to finish
            onCreateEditor={(view, state) => setTimeout(() => view.setState(state), 300)}
          />
        )}
      </Modal>
      <PdfPreviewModal title={previewOrderFile?.fileName ?? ''} url={pdfPreviewUrl} loading={pdfPreviewLoading} onClose={closeOrderFile} />
    </Space>
  ) : (
    <Typography.Text type="secondary">Keine Dokumente vorhanden</Typography.Text>
  );
};
